import type * as Api from '@ViewModels';
import type { Node } from '@xyflow/react';
import type { BaseNodeData } from '../types';
import type { ChainTerminus } from '../hooks/useChainTermini';
import { BEHAVIOR_SWITCH_LAST_STEP_MESSAGE, templateHasBehaviorSwitch } from './behaviorSwitchRules';
import { findSwitchNode, resolveBranchCaseTemplateId } from './branchNodes';

export const BRANCH_TEMPLATE_PENDING_MESSAGE =
	'This branch is still being created. Wait for saving to finish, then try again.';

type TerminusWriteTarget = { status: 'ready'; templateId: string } | { status: 'blocked'; message: string };

function getNodeTemplateId(node: Node<BaseNodeData> | null | undefined): string | undefined {
	if (!node) {
		return undefined;
	}

	const nodeData = node.data as { automationTemplate?: Api.IAutomationTemplate };
	return nodeData.automationTemplate?.id;
}

/** Returns the child template that owns a branch, or undefined until the branch is persisted. */
export function resolveBranchTemplateId(
	branchInfo: NonNullable<ChainTerminus['branchInfo']>,
	nodes: Node<BaseNodeData>[]
): string | undefined {
	const { parentSwitchId, caseIndex, caseId } = branchInfo;
	const branchTemplateId = resolveBranchCaseTemplateId(nodes, {
		parentSwitchNodeId: parentSwitchId,
		caseIndex,
		caseId,
	});
	if (branchTemplateId) {
		return branchTemplateId;
	}

	const switchNodeData = findSwitchNode(nodes, parentSwitchId)?.data as
		| { automationStep?: Api.ISwitchAutomationStep }
		| undefined;
	const cases = switchNodeData?.automationStep?.cases ?? [];
	const matchedCase = caseId ? cases.find(candidate => candidate.id === caseId) : cases[caseIndex];
	return matchedCase?.automationTemplateId;
}

export function resolveTargetTemplateIdForTerminus({
	terminus,
	nodes,
	rootTemplateId,
}: {
	terminus: ChainTerminus | null | undefined;
	nodes: Node<BaseNodeData>[];
	rootTemplateId?: string;
}): string | undefined {
	if (!terminus) {
		return rootTemplateId;
	}

	const ownTemplateId = getNodeTemplateId(terminus.node);
	if (!terminus.branchInfo) {
		return ownTemplateId || rootTemplateId;
	}

	// Follow the branch the canvas shows. A leaf's own template can still name the root.
	const branchTemplateId = resolveBranchTemplateId(terminus.branchInfo, nodes);
	if (branchTemplateId) {
		return branchTemplateId;
	}

	// Never route an unresolved branch write to the root template; the caller must wait instead.
	return ownTemplateId && ownTemplateId !== rootTemplateId ? ownTemplateId : undefined;
}

/**
 * Resolves the template a step added at `terminus` is written to, or why it cannot be written yet.
 * Add paths run this once, before their optimistic insert, and hand the result to the save layer,
 * whose copy of the nodes trails the canvas by a frame.
 */
export function resolveTerminusWriteTarget({
	terminus,
	nodes,
	rootTemplateId,
}: {
	terminus: ChainTerminus | null | undefined;
	nodes: Node<BaseNodeData>[];
	rootTemplateId?: string;
}): TerminusWriteTarget {
	const templateId = resolveTargetTemplateIdForTerminus({ terminus, nodes, rootTemplateId });
	if (!templateId) {
		return { status: 'blocked', message: BRANCH_TEMPLATE_PENDING_MESSAGE };
	}

	// The API appends new steps to the end of their template, after any Conditional Behavior.
	if (templateHasBehaviorSwitch(nodes, templateId)) {
		return { status: 'blocked', message: BEHAVIOR_SWITCH_LAST_STEP_MESSAGE };
	}

	return { status: 'ready', templateId };
}

export function buildTemplateForNode({
	rootTemplate,
	targetTemplateId,
	existingTemplate,
}: {
	rootTemplate?: Api.IAutomationTemplate;
	targetTemplateId?: string;
	existingTemplate?: Api.IAutomationTemplate;
}): Api.IAutomationTemplate | undefined {
	if (existingTemplate && (!targetTemplateId || existingTemplate.id === targetTemplateId)) {
		return existingTemplate;
	}

	if (!rootTemplate) {
		return existingTemplate;
	}

	if (targetTemplateId && rootTemplate.id !== targetTemplateId) {
		return { ...rootTemplate, id: targetTemplateId };
	}

	return rootTemplate;
}
